import {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import dayjs from 'dayjs';
import fetchData from '@/utils/fetchData';
import useError from '@/hooks/useError';

type Props = {
  children: ReactNode;
};

export type DiscountProviderType = {
  name: string;
  type: 'EXTENSION' | 'INTEREST' | 'INITIAL_COMMISSION';
  discountPercent: number;
  lumpSumDiscount: number | null;
  termRanges: Array<{
    min: number;
    max: number;
  }>;
  validityRanges: Array<{
    min: string | null;
    max: string | null;
  }>;
  amountRanges: Array<{
    min: number;
    max: number;
  }>;
  created: string;
};

export type DiscountsContextType = {
  discounts?: DiscountProviderType[];
  largestExtensionDiscount?: DiscountProviderType;
  largestInterestDiscount?: DiscountProviderType;
  fetchDiscounts: () => Promise<DiscountProviderType[] | void>;
};

export const DiscountsContext = createContext<DiscountsContextType>(
  {} as DiscountsContextType,
);

/** Finds the largest discount from web-api discount object. Optionally find largest discount by type */
const findLargestDiscount = (
  discounts: DiscountProviderType[],
  type: 'EXTENSION' | 'INTEREST' | null = null,
) => {
  if (discounts?.length) {
    return discounts.reduce(
      (accumulator: DiscountProviderType | undefined, discount) => {
        return (accumulator?.discountPercent || 0) < discount.discountPercent &&
          (type === null || type === discount.type) &&
          (discount.validityRanges[0].min === null ||
            dayjs() >= dayjs(discount.validityRanges[0].min))
          ? discount
          : accumulator;
      },
      undefined,
    );
  }
  return undefined;
};

export function DiscountsProvider({ children }: Props): React.JSX.Element {
  const [discounts, setDiscounts] = useState<DiscountProviderType[]>();
  const [largestExtensionDiscount, setLargestExtensionDiscount] =
    useState<DiscountProviderType>();
  const [largestInterestDiscount, setLargestInterestDiscount] =
    useState<DiscountProviderType>();
  const { showError } = useError();

  const fetchDiscounts = useCallback(async (): Promise<
    DiscountProviderType[] | void
  > => {
    try {
      const fetchedDiscounts = await fetchData('/client/discounts/available');

      if (fetchedDiscounts) {
        setDiscounts(fetchedDiscounts.availableDiscounts);

        setLargestExtensionDiscount(
          findLargestDiscount(fetchedDiscounts.availableDiscounts, 'EXTENSION'),
        );
        setLargestInterestDiscount(
          findLargestDiscount(fetchedDiscounts.availableDiscounts, 'INTEREST'),
        );
      }

      return fetchedDiscounts;
    } catch (e) {
      showError();
      throw e;
    }
  }, [showError]);

  const discountsContextValue = useMemo(
    () => ({
      discounts,
      largestExtensionDiscount,
      largestInterestDiscount,
      fetchDiscounts,
    }),
    [
      discounts,
      fetchDiscounts,
      largestExtensionDiscount,
      largestInterestDiscount,
    ],
  );

  return (
    <DiscountsContext.Provider value={discountsContextValue}>
      {children}
    </DiscountsContext.Provider>
  );
}
